import { useTranslations } from '@vocab/react';
import { Button, Stack, Text } from 'braid-design-system';
import { useCallback, useEffect, useState } from 'react';

import { useHotjarEvent } from 'src/hooks/useHotjarEvent';

import translations from '../.vocab';
import { CareerFeedMobileWebFeatureFlagContainer } from '../CareerFeedMobileWebFeatureFlagContainer';
import RefreshRecommendationsButton from '../RefreshRecommendationsButton';
import {
  useGetRefreshRecommendationsWithCandidateChatContext,
  useGetResetCandidateChatContext,
} from '../hooks/candidateChatContextHelpers';
import { useCareerFeedAIAssistant } from '../hooks/useCareerFeedAIAssistant';

import {
  CareerFeedAIAssistantContentFrame,
  CareerFeedAIAssistantContentFrameMobile,
} from './CareerFeedAIAssistantContentFrame';
import { RecommendationRefinedConfirmation } from './CareerFeedAIAssistantRecommendationRefinedConfirmation';
import { CareerFeedMobileModal } from './CareerFeedMobileModal';

// This hook is responsible for refreshing the recommendations when the user refines their preferences.
export const useRefreshRecsWhenRefined = () => {
  const {
    tracking: { trackRecommendedJobsRefreshed, trackGenAIChatEnded },
    progress,
  } = useCareerFeedAIAssistant();

  const refreshRecommendations =
    useGetRefreshRecommendationsWithCandidateChatContext();

  const refreshRecs = useCallback(async () => {
    const result = await refreshRecommendations();
    if (result.success) {
      trackGenAIChatEnded();
      trackRecommendedJobsRefreshed({ jobsCount: result.jobsCount });
    } else {
      trackGenAIChatEnded(Boolean(result.error));
      trackRecommendedJobsRefreshed({ jobsCount: -1 });
    }
  }, [
    refreshRecommendations,
    trackGenAIChatEnded,
    trackRecommendedJobsRefreshed,
  ]);

  const [isRefreshedRecs, setIsRefreshedRecs] = useState(false);

  useEffect(() => {
    if (progress === 'recsRefined' && !isRefreshedRecs) {
      setIsRefreshedRecs(true);
      refreshRecs();
    }

    return () => {
      if (progress !== 'recsRefined') {
        setIsRefreshedRecs(false);
      }
    };
  }, [isRefreshedRecs, progress, refreshRecs]);
};

export const ConversationFinished = () => {
  const resetPreferences = useGetResetCandidateChatContext();
  const {
    lastCompletedAssistantResponse,
    tracking: { trackGenAIChatResetPressed },
    closeMobileModal,
  } = useCareerFeedAIAssistant();
  const { t } = useTranslations(translations);

  useHotjarEvent('homepage_genAI_end');

  const onReset = useCallback(() => {
    trackGenAIChatResetPressed('chat widget');
    resetPreferences();
  }, [resetPreferences, trackGenAIChatResetPressed]);

  const message = lastCompletedAssistantResponse?.text ?? '';

  return (
    <CareerFeedMobileWebFeatureFlagContainer
      mobileView={
        <>
          <CareerFeedMobileModal>
            <CareerFeedAIAssistantContentFrameMobile
              headMessage={message}
              errorType={null}
              contentSection={<></>}
              ctaSection={
                <Button
                  variant="solid"
                  tone="brandAccent"
                  onClick={closeMobileModal}
                >
                  {t('Discover your recommendations')}
                </Button>
              }
              loading={
                false /* Loading is just used for loading overlay, we don't want this on the final q */
              }
            />
          </CareerFeedMobileModal>
          <RecommendationRefinedConfirmation />
        </>
      }
      tabletAndAboveView={
        <CareerFeedAIAssistantContentFrame
          widgetHeadMessage={message}
          widgetContentSection={<></>}
          widgetCtaSection={
            <Stack space="small">
              <RefreshRecommendationsButton onReset={onReset} />
              <Text size="xsmall" tone="secondary">
                {t('These AI-powered job suggestions....')}
              </Text>
            </Stack>
          }
          loadingOnWidget={
            false /* Loading is just used for loading overlay, we don't want this on the final q */
          }
          loadingOnDrawer={
            false /* Loading is just used for loading overlay, we don't want this on the final q */
          }
        />
      }
    />
  );
};
